import { ComponentType, FC } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface ErrorPageAction {
  label: string;
  icon?: ComponentType<any>;
  onClick: () => void;
  variant?: 'solid' | 'outline' | 'ghost';
  colorScheme?: string;
  size?: 'sm' | 'md' | 'lg';
}

export interface ErrorPageProps {
  title: string;
  subtitle: string;
  description: string;
  icon?: ComponentType<any>;
  iconColor?: string;
  iconBgColor?: string;
  actions?: ErrorPageAction[];
  maxWidth?: string;
  showIllustration?: boolean;
}

export const ErrorPage: FC<ErrorPageProps> = ({
  title,
  subtitle,
  description,
  icon,
  iconColor = 'blue.500',
  iconBgColor = 'blue.100',
  actions = [],
  maxWidth = 'lg',
  showIllustration = true,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const bgColor = useColorModeValue('gray.50', 'gray.900');

  const defaultActions: ErrorPageAction[] = [
    {
      label: t('actions.back'),
      icon: undefined,
      onClick: () => navigate(-1),
      variant: 'outline',
      size: 'md',
    },
    {
      label: t('actions.home'),
      icon: undefined,
      onClick: () => navigate('/'),
      variant: 'ghost',
      size: 'md',
    },
  ];

  const finalActions = actions.length > 0 ? actions : defaultActions;

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg={bgColor}
      px={4}
    >
      <Box
        maxW={maxWidth}
        w="full"
        textAlign="center"
        p={8}
      >
        {/* Illustration */}
        {showIllustration && icon && (
          <Box mb={8}>
            <Box
              w="120px"
              h="120px"
              mx="auto"
              borderRadius="full"
              bg={iconBgColor}
              display="flex"
              alignItems="center"
              justifyContent="center"
              mb={4}
            >
              <Icon
                as={icon}
                w="60px"
                h="60px"
                color={iconColor}
              />
            </Box>
          </Box>
        )}

        {/* Content */}
        <VStack spacing={4} mb={8}>
          <Heading
            size="xl"
            color="gray.800"
            _dark={{ color: 'white' }}
          >
            {title}
          </Heading>

          <Text
            fontSize="lg"
            color="blue.600"
            fontWeight="medium"
          >
            {subtitle}
          </Text>

          <Text
            color="gray.600"
            _dark={{ color: 'gray.400' }}
            lineHeight="tall"
          >
            {description}
          </Text>
        </VStack>

        {/* Action Buttons */}
        {finalActions.length > 0 && (
          <VStack spacing={3}>
            {finalActions.length === 1 ? (
              <Button
                colorScheme={finalActions[0].colorScheme || 'blue'}
                variant={finalActions[0].variant || 'solid'}
                size={finalActions[0].size || 'lg'}
                w="full"
                leftIcon={finalActions[0].icon ? <Icon as={finalActions[0].icon} /> : undefined}
                onClick={finalActions[0].onClick}
              >
                {finalActions[0].label}
              </Button>
            ) : finalActions.length === 2 ? (
              <HStack spacing={3} w="full">
                <Button
                  variant={finalActions[0].variant || 'outline'}
                  colorScheme={finalActions[0].colorScheme}
                  size={finalActions[0].size || 'md'}
                  flex={1}
                  leftIcon={finalActions[0].icon ? <Icon as={finalActions[0].icon} /> : undefined}
                  onClick={finalActions[0].onClick}
                >
                  {finalActions[0].label}
                </Button>

                <Button
                  variant={finalActions[1].variant || 'ghost'}
                  colorScheme={finalActions[1].colorScheme}
                  size={finalActions[1].size || 'md'}
                  flex={1}
                  leftIcon={finalActions[1].icon ? <Icon as={finalActions[1].icon} /> : undefined}
                  onClick={finalActions[1].onClick}
                >
                  {finalActions[1].label}
                </Button>
              </HStack>
            ) : (
              <VStack spacing={2} w="full">
                {finalActions.map((action, index) => (
                  <Button
                    key={index}
                    colorScheme={action.colorScheme || (index === 0 ? 'blue' : undefined)}
                    variant={action.variant || (index === 0 ? 'solid' : 'outline')}
                    size={action.size || 'md'}
                    w="full"
                    leftIcon={action.icon ? <Icon as={action.icon} /> : undefined}
                    onClick={action.onClick}
                  >
                    {action.label}
                  </Button>
                ))}
              </VStack>
            )}
          </VStack>
        )}
      </Box>
    </Flex>
  );
}; 